import React, {
  useCallback,
  useMemo,
} from 'react';

import {
  RecordContextProvider,
  useGetList,
  Button,
  usePermissions,
} from 'react-admin';

import {
  Box,
  Card,
  CardContent,
  Collapse,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';

import ArrowBackIosFilled from '@mui/icons-material/ArrowBackIos';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import {
  Link,
  useNavigate,
} from 'react-router-dom';

import {
  isArray,
  isEmpty,
  isPlainObject,
} from 'lodash';

import ConflictResolutionTitle from './ConflictResolutionTitle';
import ConflictResolutionSources from './ConflictResolutionSources';

import fieldStatus from '../../constants/fieldStatus';
import ConflictVerifiedStatus from './ConflictVerifiedStatus';
import conflictFieldsLabel from './conflictFieldsLabels';
import { ConflictFields } from '../../constants/conflictFields';
import languages from '../../constants/languages';
import conflictFieldsAnswerFields from './conflictFieldsAnswerField';
import useDialogStatus from '../../hooks/useDialogStatus';
import useConflictContext from './useConflictContext';
import { EntityTypes } from '../../constants/entityTypes';
import hasAccess from '../../utilities/hasAccess';
import actions, { Actions } from '../../constants/actions';

export const DEFAULT_SX = {
  wordBreak: 'break-all',
  wordWrap: 'normal',
};

export type DatapointValue = {
  id: string;
  entity: {
    id: string;
    type: EntityTypes;
  };
  selected: boolean;
  updatedAt: Date;
  createdAt: Date;
  name: ConflictFields;
  value: unknown;
  sourceNames: string[];
  maximumMatchProbability?: number;
}

const ListItem = ({
  item,
  otherSelected,
  customValueActions,
}: {
  item: DatapointValue;
  otherSelected: boolean;
  customValueActions?: React.ReactNode;
}) => {
  const valueIsComplex = (isArray(item.value) && item.value.every(isPlainObject))
  || isPlainObject(item.value);

  const bgcolor = item.selected ? 'greenGray.main' : 'background.grey';

  const {
    searchParams,
    name,
  } = useConflictContext();

  const {
    open: shouldShowSources,
    openDialog: showSources,
    closeDialog: hideSources,
  } = useDialogStatus();

  return (
    <Grid
      bgcolor={bgcolor}
      size={12}
    >
      <Grid container spacing={4} p={6} justifyContent="space-between" display="flex" alignItems="center">
        <Grid size={valueIsComplex ? 12 : 4}>
          <RecordContextProvider value={{ ...item, sx: DEFAULT_SX }}>
            {React.createElement(
              conflictFieldsAnswerFields[name],
              { language: languages.EN },
            )}
          </RecordContextProvider>
        </Grid>
        <Grid size={12}>
          <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={4} alignItems="center">
            <Box display="flex" gap={4} justifyContent="center" alignItems="center" flexWrap="wrap">
              <Typography variant="h4" color="gray">{item.sourceNames.join(', ') }</Typography>
              {shouldShowSources
                ? (
                  <Button
                    onClick={hideSources}
                    startIcon={<VisibilityOffIcon />}
                    label="Hide sources"
                    size="medium"
                  />
                ) : (
                  <Button
                    onClick={showSources}
                    startIcon={<VisibilityIcon />}
                    size="medium"
                    label="View sources"
                  />
                )}
            </Box>
            <Box display="flex" gap={4} justifyContent="center" alignItems="center" flexWrap="wrap">
              <RecordContextProvider value={item}>
                {customValueActions}
              </RecordContextProvider>
              <ConflictVerifiedStatus
                selected={item.selected}
                otherSelected={otherSelected}
                toMarkVerified={`mark-verified/${item.id}?${searchParams.toString()}`}
              />
            </Box>
          </Box>
        </Grid>
        <Grid size={12}>
          <RecordContextProvider value={item}>
            <Collapse in={shouldShowSources}>
              <ConflictResolutionSources />
            </Collapse>
          </RecordContextProvider>
        </Grid>
      </Grid>
    </Grid>
  );
};

const ValueList = ({
  data,
  customValueActions,
}: {
  data?: DatapointValue[];
  name: ConflictFields;
  identificationInfo: unknown;
  customValueActions?: React.ReactNode;
}) => {
  const selected = useMemo(() => data?.filter((item) => item.selected), [data]);

  return (
    <Grid size={12}>
      <Grid container spacing={4}>
        {isEmpty(data) && (
          <Grid size={12}>
            <Card
              variant="outlined"
              sx={{ m: 0 }}
            >
              <CardContent>
                <Typography>No data available</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        {data?.map((item) => (
          <Grid
            key={item.id}
            size={12}
          >
            <ListItem
              item={item}
              otherSelected={!isEmpty(selected?.filter((selection) => selection.id !== item.id))}
              customValueActions={customValueActions}
            />
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};

const ConflictValueListView = ({
  customValueActions,
  hideBackButton,
}: {
  customValueActions?: React.ReactNode;
  hideBackButton: boolean;
}) => {
  const {
    entityId,
    entityType,
    identificationInfo,
    name,
    searchParams,
  } = useConflictContext();
  const { permissions } = usePermissions<Actions[]>();

  const navigate = useNavigate();
  const handelBack = useCallback(() => {
    navigate('..');
  }, [navigate]);

  const { data, isLoading } = useGetList<DatapointValue>('data-points', {
    filter: {
      name,
      entityId,
      entityType,
      identificationInfo: JSON.stringify(identificationInfo),
    },
  });
  const status = useMemo(() => {
    if (!data?.length) return fieldStatus.EMPTY;

    if (data.length === 1) return fieldStatus.RESOLVED;
    if (data.some((dataItem) => dataItem.selected)) return fieldStatus.RESOLVED;

    return fieldStatus.CONFLICT;
  }, [data]);

  if (!name || !entityId || !entityType) return null;

  if (isLoading) return null;

  return (
    <Grid container gap={1}>
      {!hideBackButton && (
        <Grid
          mb={3}
          size={12}
        >
          <Box display="flex" alignItems="center">
            <IconButton size="medium" onClick={handelBack}>
              <ArrowBackIosFilled fontSize="medium" color="primary" />
            </IconButton>
            <Typography color="primary" variant="h5">Back to form</Typography>
          </Box>
        </Grid>
      )}
      <Grid
        sx={{ backgroundColor: 'background.paper' }}
        size={12}
      >
        <Card
          variant="outlined"
          sx={{ margin: 0 }}
        >
          <CardContent>
            <Grid container spacing={6}>
              <Grid size={12}>
                <ConflictResolutionTitle
                  label={conflictFieldsLabel[name]}
                  status={status}
                />
              </Grid>
              <ValueList
                name={name}
                data={data}
                identificationInfo={identificationInfo}
                customValueActions={customValueActions}
              />
              {hasAccess(permissions, actions.DATA_POINT_CREATE) && (
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Button
                    label="+ Add value manually"
                    to={`manual-input?${searchParams.toString()}`}
                    size="medium"
                    component={Link}
                  />
                </Grid>
              )}
              {hasAccess(permissions, actions.RESOLUTION_HISTORY_GET) && (
                <Grid
                  display="flex"
                  justifyContent="end"
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Button
                    startIcon={<HistoryIcon />}
                    component={Link}
                    size="medium"
                    label="View resolution history"
                    to={`history?${searchParams.toString()}`}
                  />
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ConflictValueListView;
